import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ChatMessage {
	id: string;
	conversation_id: string;
	sender_id: string | null;
	kind: 'user' | 'system' | 'team_reply';
	body: string;
	attachments: string[];
	created_at: string;
}

interface State {
	conversationId: string | null;
	messages: ChatMessage[];
	loading: boolean;
	error: string | null;
	unread: boolean;
}

/**
 * Loads (or lazily creates on first send) the signed-in user's
 * team_support conversation and subscribes to new messages.
 */
export const useTeamConversation = () => {
	const { user } = useAuth();
	const [state, setState] = useState<State>({
		conversationId: null,
		messages: [],
		loading: false,
		error: null,
		unread: false,
	});

	// Fetch existing team conversation for this user (if any)
	useEffect(() => {
		if (!user) {
			setState({ conversationId: null, messages: [], loading: false, error: null, unread: false });
			return;
		}
		let cancelled = false;
		(async () => {
			setState((s) => ({ ...s, loading: true }));
			// Find a team_support conversation this user participates in
			const { data: parts, error: pErr } = await supabase
				.from('chat_participants')
				.select('conversation_id, last_read_at, chat_conversations!inner(id, kind, last_message_at)')
				.eq('user_id', user.id)
				.eq('chat_conversations.kind', 'team_support')
				.limit(1)
				.maybeSingle();
			if (cancelled) return;
			if (pErr) {
				setState((s) => ({ ...s, loading: false, error: pErr.message }));
				return;
			}
			const convId = parts?.conversation_id ?? null;
			let messages: ChatMessage[] = [];
			let unread = false;
			if (convId) {
				const { data: msgs } = await supabase
					.from('chat_messages')
					.select('*')
					.eq('conversation_id', convId)
					.order('created_at', { ascending: true });
				messages = (msgs ?? []) as ChatMessage[];
				const lastRead = parts?.last_read_at ? new Date(parts.last_read_at).getTime() : 0;
				const lastMsg = parts?.chat_conversations?.last_message_at
					? new Date(parts.chat_conversations.last_message_at).getTime()
					: 0;
				unread = lastMsg > lastRead;
			}
			setState({ conversationId: convId, messages, loading: false, error: null, unread });
		})();
		return () => {
			cancelled = true;
		};
	}, [user]);

	// Realtime: subscribe to messages in active conversation
	useEffect(() => {
		if (!state.conversationId) return;
		const channel = supabase
			.channel(`chat-thread-${state.conversationId}`)
			.on(
				'postgres_changes',
				{
					event: 'INSERT',
					schema: 'public',
					table: 'chat_messages',
					filter: `conversation_id=eq.${state.conversationId}`,
				},
				(payload) => {
					const m = payload.new as ChatMessage;
					setState((s) => {
						if (s.messages.some((x) => x.id === m.id)) return s;
						return { ...s, messages: [...s.messages, m], unread: m.kind === 'team_reply' };
					});
				}
			)
			.subscribe();
		return () => {
			supabase.removeChannel(channel);
		};
	}, [state.conversationId]);

	const markRead = useCallback(async () => {
		if (!user || !state.conversationId) return;
		await supabase
			.from('chat_participants')
			.update({ last_read_at: new Date().toISOString() })
			.eq('conversation_id', state.conversationId)
			.eq('user_id', user.id);
		setState((s) => ({ ...s, unread: false }));
	}, [user, state.conversationId]);

	const onMessageSent = useCallback(
		(msg: ChatMessage, conversationId: string) => {
			setState((s) => ({
				...s,
				conversationId,
				messages: s.messages.some((x) => x.id === msg.id) ? s.messages : [...s.messages, msg],
			}));
		},
		[]
	);

	return {
		...state,
		markRead,
		onMessageSent,
	};
};
